import {Injectable} from '@angular/core';
import {BaseService} from '@shared/services/api/base-service';
import {AceLogger} from '@logging/ace-logger';
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {AceLoggerService} from '@logging/ace-logger.service';
import VariableOptions from 'gql-query-builder/build/VariableOptions';
import {Device, Equipment, EquipmentId, EquipmentProperty, Favourite, Long, MetadataType, MetadataTypeId, VirtualFile, Zone} from '@app/graphql-typings/graphql';
import {Observable, of} from 'rxjs';
import * as gql from 'gql-query-builder';
import {PageInfo} from '@shared/components/ace-table/ace-table.component';
import Fields from 'gql-query-builder/build/Fields';
import {PagedModel} from '@shared/api/model/paged-model';
import {map, tap} from 'rxjs/operators';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import moment from 'moment-timezone';
import {APIResponse} from "@app/layout/admin/services/api/api.interface";
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {objectToHttpParams} from '@app/shared/util/angular/angular-utils';
import {SiteId} from './access-sites.interface';

export const WEEKS_OF_OPERATION_MIN = 1;
export const WEEKS_OF_OPERATION_MAX = 52;

export interface EquipmentSearchCriteria {
  site_id?: number;
  site_ids?: number[];
  equipment_id?: number;
  equipment_ids?: number[];
  collector_id?: number;
  collector_ids?: number[];
  provider_id?: number;
  provider_ids?: number[];
  name?: string;
  partial_name_search?: boolean;
  vendor?: string;
  model?: string;
  level?: string;
  levels?: string[];
  level_id?: number;
  level_ids?: number[];
  zone_name_id?: number;
  zone_name_ids?: number[];
  zone?: string;
  zones?: string[];
  zone_id?: number;
  zone_ids?: number[];
  shared?: boolean;
  primary_device_id?: number;
  primary_device_ids?: number[];
  metadata_type_id?: number;
  metadata_type_ids?: number[];
  metadata_type_code?: string;
  metadata_type_codes?: string[];
  file_id?: number;
  system?: boolean;
  commissionable?: boolean;
  is_active?: boolean;
  start_index?: number;
  limit?: number;
}

export interface MinimalEquipmentFetchOptions {
  includeLevelZone?: boolean;
}

export class EquipmentScopeOptions {
  getFavouritesData?: boolean;
  getDeviceData?: boolean;
  getFilesData?: boolean;
  getParentEquipment?: boolean;
  getAssociatedEquipmentIds?: boolean;
  getCollectorData?: boolean;
  /** Implies `getCollectorData`, as the site sits under the collector */
  getCollectorSite?: boolean;
  getEquipmentProperties?: boolean;
  getExternalReferences?: boolean;
}

export interface EquipmentResponse<T> {
  equipment: T
}

export interface EquipmentDeletedResponse {
  deleted: boolean;
  equipment_id: number;
}

export enum EquipmentAggregationEntity {
  SITE = 'site',
  ALL = 'all'
}
export interface EquipmentAggregationSearchTerm {
  site_id?: number;
  site_ids?: number[];
  start_index?: number;
  limit?: number;
  aggregate_entity: EquipmentAggregationEntity;
}

export interface EquipmentAggregationResponse {
  equipment_aggregation: EquipmentAggregation[];
}

export interface EquipmentAggregation {
  count: number;
  site_id?: number;
}

interface EquipmentAPI {
  name: string;
  zone_id?: number;
  is_active?: boolean;
  files?: number[];
  shared?: boolean;
  virtual?: boolean;
  metadata_type_id?: number;
  role?: string;
  vendor?: string;
  favourite_ids?: Long[];
  canonical_favourite_ids?: Long[];
  canonical_device_ids?: Long[];
  related_equipment?:  Long[];
  last_modified_at?: string;
  capacity?: number;
  capacity_unit?: string;
  energyconsumption?: number;
  energyconsumption_unit?: string;
  equipment_id?: Long;
  comment?: string;
  collector_id: number;
  model?: string;
  primary_device_id?: number;
  target_running_load?: number;
  parent_equipment_ids?: number[];
  maintainer_name?: string;
  maintainer_reference?: string;
  operation_duration_per_year?: string | null;
}


@Injectable({
  providedIn: 'root'
})
export class EquipmentService extends BaseService {
  private headers: HttpHeaders = this.getHeaders();

  private readonly logger: AceLogger;
  private readonly GRAPHQL_FETCH_PAGE_SIZE = 1000;
  private readonly EQUIPMENT_OPERATION = 'equipment';
  private readonly EQUIPMENT_PROPERTIES_KEY = 'equipment_properties';
  private readonly GRAPHQL_VARIABLES: VariableOptions = {
    site_id: { type: GRAPHQL_TYPES.INTEGER },
    site_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
    equipment_id: { type: GRAPHQL_TYPES.LONG },
    equipment_ids: { type: GRAPHQL_TYPES.LONG, list: true },
    collector_id: { type: GRAPHQL_TYPES.INTEGER },
    collector_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
    provider_id: { type: GRAPHQL_TYPES.INTEGER },
    provider_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
    name: { type: GRAPHQL_TYPES.STRING },
    vendor: { type: GRAPHQL_TYPES.STRING },
    model: { type: GRAPHQL_TYPES.STRING },
    level: { type: GRAPHQL_TYPES.STRING },
    levels: { type: GRAPHQL_TYPES.STRING, list: true },
    level_id: { type: GRAPHQL_TYPES.LONG },
    level_ids: { type: GRAPHQL_TYPES.LONG, list: true },
    zone_name_id: { type: GRAPHQL_TYPES.LONG },
    zone_name_ids: { type: GRAPHQL_TYPES.LONG, list: true },
    zone: { type: GRAPHQL_TYPES.STRING },
    zones: { type: GRAPHQL_TYPES.STRING, list: true },
    zone_id: { type: GRAPHQL_TYPES.LONG },
    zone_ids: { type: GRAPHQL_TYPES.LONG, list: true },
    shared: { type: GRAPHQL_TYPES.BOOLEAN },
    virtual: { type: GRAPHQL_TYPES.BOOLEAN },
    primary_device_id: { type: GRAPHQL_TYPES.LONG },
    primary_device_ids: { type: GRAPHQL_TYPES.LONG, list: true },
    metadata_type_id: { type: GRAPHQL_TYPES.INTEGER },
    metadata_type_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
    metadata_type_code: { type: GRAPHQL_TYPES.STRING },
    metadata_type_codes: { type: GRAPHQL_TYPES.STRING, list: true },
    file_id: { type: GRAPHQL_TYPES.INTEGER },
    system: { type: GRAPHQL_TYPES.BOOLEAN },
    commissionable: { type: GRAPHQL_TYPES.BOOLEAN },
    is_active: { type: GRAPHQL_TYPES.BOOLEAN },
    start_index: { type: GRAPHQL_TYPES.INTEGER, required: true },
    limit: { type: GRAPHQL_TYPES.INTEGER, required: true },
  };
  private readonly LEVEL_ZONE_FIELDS = [
    'zone_id',
    {
      zone: [
        'zone_id', 'system',
        {level: ['level_name', 'level_id', 'system']},
        {zone_name: ['zone_name', 'zone_name_id', 'system']}
      ]
    }
  ];

  private readonly COLLECTOR_FIELDS = ['collector_id', 'hostname', 'site_id'];

  private readonly EQUIPMENT_FIELDS = [
    'equipment_id',
    'canonical_favourite_ids',
    'favourite_ids',
    'is_active',
    ...this.LEVEL_ZONE_FIELDS,
    'capacity',
    'capacity_unit',
    'energyconsumption',
    'energyconsumption_unit',
    'target_running_load',
    'name',
    'related_equipment',
    'shared',
    'virtual',
    'capacity',
    'capacity_unit',
    'role',
    'model',
    'vendor',
    'collector_id',
    'primary_device_id',
    'comment',
    'metadata_type_id',
    'parent_equipment_ids',
    'maintainer_name',
    'maintainer_reference',
    'operation_duration_per_year'
  ];

  private readonly MINIMAL_EQUIPMENT_FIELDS = [
    'equipment_id',
    'name',
    'metadata_type_id',
    'operation_duration_per_year'
  ];

  constructor(private httpUtilityService: HttpUtilityService,
              private http: HttpClient,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(EquipmentService.name);
  }

  public filterEquipmentByIdByMetadataTypeIds(equipmentById: Map<EquipmentId, Equipment>, metadataTypeIds: MetadataTypeId[]): Map<number, Equipment> {
    let equipmentOptionsById: Map<EquipmentId, Equipment>;
    if (metadataTypeIds?.length) {
      const metadataTypeIdSet = new Set<MetadataTypeId>(metadataTypeIds);
      equipmentOptionsById = new Map();
      equipmentById?.forEach((equipmentItem, equipmentId) => {
        if (metadataTypeIdSet.has(equipmentItem.metadata_type_id)) {
          equipmentOptionsById.set(equipmentId, equipmentItem);
        }
      });
    } else {
      equipmentOptionsById = new Map(equipmentById);
    }
    return equipmentOptionsById;
  }

  private getEquipmentPagedModel(fields: Fields, searchCriteria: EquipmentSearchCriteria, pageInfo: PageInfo): Observable<PagedModel<Equipment[]>> {
    const query = gql.query({
      operation: this.EQUIPMENT_OPERATION,
      fields,
      variables: this.GRAPHQL_VARIABLES
    });

    return this.httpUtilityService.graphQL<Equipment[]>(query, this.EQUIPMENT_OPERATION, {
      start_index: pageInfo.first,
      limit: pageInfo.rows,
      ...searchCriteria
    });
  }

  public getAllEquipment(fields: Fields,
                          searchCriteria: EquipmentSearchCriteria,
                          fetchPageSize?: number,
                          progressCallback?: (a: number) => void): Observable<Equipment[]> {
    let completedSoFar = 0;
    const searchWrapper = (pageInfo: PageInfo) => this.getEquipmentPagedModel(fields, searchCriteria, pageInfo)
      .pipe(tap((result: PagedModel<Equipment[]>) => {
        if (typeof progressCallback === 'function') {
          completedSoFar += result.data.length;
          const percentageSoFar = (completedSoFar/result.count) * 100;
          progressCallback(percentageSoFar);
        }
      }));
    return this.httpUtilityService.pageUntilComplete(searchWrapper, {first: 0, rows: fetchPageSize ?? this.GRAPHQL_FETCH_PAGE_SIZE});
  }

  public getEquipmentMinimalFields(searchCriteria: EquipmentSearchCriteria, pageInfo: PageInfo): Observable<PagedModel<Equipment[]>> {
    return this.getEquipmentPagedModel(this.MINIMAL_EQUIPMENT_FIELDS, searchCriteria, pageInfo);
  }

  public getAllEquipmentMinimalFieldsBySiteIds(searchCriteria: EquipmentSearchCriteria, fetchPageSize?: number, options?: MinimalEquipmentFetchOptions): Observable<Equipment[]> {
    const fields = [
      ...this.MINIMAL_EQUIPMENT_FIELDS,
      {collector: ['site_id']},
      ...(options?.includeLevelZone ? this.LEVEL_ZONE_FIELDS : [])
    ];
    return this.getAllEquipment(fields, searchCriteria, fetchPageSize);
  }

  // minimal fields
  public getEquipmentByIdBySiteIds(siteIds: SiteId[], options?: MinimalEquipmentFetchOptions): Observable<Map<SiteId, Map<EquipmentId, Equipment>>> {
    const equipmentBySiteIdFetch = (siteIdBatch: SiteId[]): Observable<[SiteId, Map<EquipmentId, Equipment>][]> =>
      this.getAllEquipmentMinimalFieldsBySiteIds({site_ids: siteIdBatch}, null, options)
        .pipe(map(equipment => [[siteIdBatch[0], CollectionUtils.indexBy(equipment, 'equipment_id')]]));
    return this.httpUtilityService.requestPartitioned(equipmentBySiteIdFetch, siteIds, 1)
      .pipe(map(siteIdEquipmentTuples => new Map(siteIdEquipmentTuples)));
  }

  private getEquipmentFields(scopeOptions?: EquipmentScopeOptions): Fields {
    const fetchScopeOptions: EquipmentScopeOptions = scopeOptions ?? {};
    return [
      ...this.EQUIPMENT_FIELDS,
      ...fetchScopeOptions.getCollectorData || fetchScopeOptions.getCollectorSite
        ? [{collector: fetchScopeOptions.getCollectorSite
          ? [...this.COLLECTOR_FIELDS, {site: ['site_id', 'site_name']}]
          : this.COLLECTOR_FIELDS}]
        : [],
      ...fetchScopeOptions.getAssociatedEquipmentIds ? ['ancestor_equipment_ids', 'descendant_equipment_ids'] : [],
      ...fetchScopeOptions.getParentEquipment ? [{ parent_equipment: ['equipment_id', 'name', 'parent_equipment_ids'] }] : [],
      ...fetchScopeOptions.getFilesData ? ['file_ids'] : [],
      ...fetchScopeOptions.getDeviceData ? [{ primary_device: ['vendor', 'model', 'device_id', 'bacnet_device_id', 'bacnet_device_name', 'lastseen_at'] }] : [],
      ...fetchScopeOptions.getFavouritesData ? [{
        favourites: ['fav_id', 'device_object_id', 'zone_id', 'identifier', 'canonical_equipment_id', 'device_id',
          { zone: ['zone_id', { level: ['level_name'] }, { zone_name: ['zone_name'] }] },
          {
            device_object: [
                'device_object_id', 'object_name', 'object_identifier', 'units', 'device_id',
                { device: ['bacnet_device_name', 'bacnet_device_id', 'device_id'] }
            ]
          },
          { metadata: ['metadata_id', 'type_id', 'code', 'name'] }
        ]
      }] : [],
      ...fetchScopeOptions.getEquipmentProperties ? [{ equipment_properties: ['property_name', 'value'] }] : [],
      ...fetchScopeOptions.getExternalReferences
        ? [{external_references: ['equipment_reference_id', 'label', 'external_reference']}] : []
    ];
  }

  public getEquipmentById(equipmentId: number, scopeOptions?: EquipmentScopeOptions): Observable<Equipment> {
    return this.searchAllEquipment({equipment_id: equipmentId, start_index: 0, limit: 1}, scopeOptions)
      .pipe(map((equipment: Equipment[]) => CollectionUtils.only(equipment)));
  }

  public searchAllEquipment(searchCriteria: EquipmentSearchCriteria, scopeOptions?: EquipmentScopeOptions, progressCallback?: (a: number) => void): Observable<Equipment[]> {
    const fields = [
      ...this.getEquipmentFields(scopeOptions),
      {metadata_type: ['type', 'type_id', 'type_code']}
    ];

    const modifiedSearchCriteria: EquipmentSearchCriteria = {...searchCriteria};
    if (searchCriteria.name) {
      modifiedSearchCriteria.name = '%' + searchCriteria.name + '%';
    }

    return this.getAllEquipment(fields, modifiedSearchCriteria, null, progressCallback).pipe(
      map((equipmentArray: Equipment[]) => {
        equipmentArray.forEach((equipment: Equipment) => {
          if (equipment && equipment.primary_device && equipment.primary_device.lastseen_at) {
            equipment.primary_device.lastseen_at = moment(equipment.primary_device.lastseen_at);
          }
          /*TODO - Remove code to delete identifier when API no longer includes identifier in bacnet favourite responses */
          if (equipment.favourites) {
            equipment.favourites.forEach((favourite: Favourite) => {
              if (favourite.device_object_id != null) {
                delete favourite.identifier;
              }
            });
          }
        });
        return equipmentArray?.sort(this.sortByEquipmentName) ?? [];
      })
    );
  }

  /** TODO remove in favour of `getEquipmentByIds`, which covers these fields through `getCollectorSite` and keeps one field list */
  public getEquipmentDetails(equipmentIds: number[]): Observable<Equipment[]> {
    const fields = [
      ...this.EQUIPMENT_FIELDS,
      { collector: [{ site: ['site_id', 'site_name'] }] }
    ];
    return equipmentIds.length
      ? this.getAllEquipment(fields, {equipment_ids: equipmentIds})
      : of([]);
  }

  public getEquipmentByIds(equipmentIds: number[], optionalArgs?: {scopeOptions?: EquipmentScopeOptions; progressCallback?: (a: number) => void}): Observable<Equipment[]> {
    if (equipmentIds?.length) {
      const fields = this.getEquipmentFields(optionalArgs?.scopeOptions);
      let completedSoFar = 0;
      const wrapper = (partitionedEquipmentIds: number[]) => this.getEquipmentPagedModel(fields,
         {equipment_ids: partitionedEquipmentIds}, {first:0, rows: Math.min(partitionedEquipmentIds.length, this.GRAPHQL_FETCH_PAGE_SIZE)})
        .pipe(map(({data: data}: PagedModel<Equipment[]>) => {
          if (typeof optionalArgs?.progressCallback === 'function') {
            completedSoFar += data.length;
            optionalArgs.progressCallback(completedSoFar);
          }
          return data;
        }));
      return this.httpUtilityService.requestPartitioned(wrapper, equipmentIds, this.GRAPHQL_FETCH_PAGE_SIZE);
    } else {
      return of([]);
    }
  }

  public getEquipmentForSites(siteIds: number[], shared = false, scopeOptions?: EquipmentScopeOptions): Observable<Equipment[]> {
    const fields = this.getEquipmentFields(scopeOptions);
    return siteIds.length
        ? this.getAllEquipment(fields, { site_ids: siteIds, shared })
        : of([]);
  }

  public getEquipmentForCollectors(collectorIds: number[], shared = false): Observable<Equipment[]> {
    return collectorIds.length
      ? this.getAllEquipment(this.EQUIPMENT_FIELDS, { collector_ids: collectorIds, shared })
      : of([]);
  }

  // Delete an equipment
  public deleteEquipment(equipmentId: number): Observable<EquipmentResponse<EquipmentDeletedResponse>> {
    const options = { headers: this.headers };
    return this.http.delete<APIResponse<EquipmentResponse<EquipmentDeletedResponse>>>(this.getEquipmentItemEndpoint(equipmentId), options)
      .pipe(map((response: APIResponse<EquipmentResponse<EquipmentDeletedResponse>>) =>
        this.httpUtilityService.extractResponseData<EquipmentResponse<EquipmentDeletedResponse>>(response, this.EQUIPMENT_OPERATION)));
  }

  public upsertEquipment(equipments: Equipment[]): Observable<Equipment[]> {
    const equipmentsAPI = equipments.map((equipment: Equipment) => this.coerceEquipmentToEquipmentAPI(equipment));
    const options = { headers: this.headers };
    return this.http.post<APIResponse<EquipmentAPI[]>>(this.getEquipmentEndpoint(), equipmentsAPI, options).pipe(
      map(response => response.data['equipment'].map((equipment: EquipmentAPI) => this.coerceEquipmentAPIToEquipment(equipment))));
  }


  public updateEquipment(equipment: Equipment): Observable<Equipment> {
    const equipmentsAPI = this.coerceEquipmentToEquipmentAPI(equipment);
    const options = { headers: this.headers };
    return this.http.put<APIResponse<EquipmentAPI>>(this.getEquipmentItemEndpoint(equipmentsAPI.equipment_id), equipmentsAPI, options)
      .pipe(map(response => this.coerceEquipmentAPIToEquipment(response.data['equipment'])));
  }

  // Upsert provided properties, leaving omitted ones untouched.
  public patchEquipmentProperties(equipmentId: EquipmentId, properties: EquipmentProperty[]): Observable<EquipmentProperty[]> {
    const options = {headers: this.headers};
    return this.http.patch<APIResponse<{equipment_properties: EquipmentProperty[]}>>(
      this.getEquipmentPropertiesEndpoint(equipmentId), {[this.EQUIPMENT_PROPERTIES_KEY]: properties}, options)
      .pipe(map(response => this.httpUtilityService.extractResponseData<EquipmentProperty[]>(response, this.EQUIPMENT_PROPERTIES_KEY)));
  }

  private coerceEquipmentToEquipmentAPI(equipment: Equipment): EquipmentAPI {
    /* EquipmentAPI fields not available in Equipment
          canonical_device_ids?: Long[];
          last_modified_at?: string;
    */
    return {
      equipment_id: equipment.equipment_id,
      name: equipment.name,
      zone_id: equipment.zone_id,
      is_active: equipment.is_active,
      files: equipment.file_ids,
      shared: equipment.shared,
      virtual: equipment.virtual,
      metadata_type_id: equipment.metadata_type_id,
      role: equipment.role,
      vendor: equipment.vendor,
      favourite_ids: equipment.favourite_ids,
      canonical_favourite_ids: equipment.canonical_favourite_ids,
      related_equipment: equipment.related_equipment,
      capacity: equipment.capacity,
      capacity_unit: equipment.capacity_unit,
      energyconsumption: equipment.energyconsumption,
      energyconsumption_unit: equipment.energyconsumption_unit,
      comment: equipment.comment,
      collector_id: equipment.collector_id,
      model: equipment.model,
      primary_device_id: equipment.primary_device_id,
      target_running_load: equipment.target_running_load,
      parent_equipment_ids: equipment.parent_equipment_ids,
      maintainer_name: equipment.maintainer_name,
      maintainer_reference: equipment.maintainer_reference,
      operation_duration_per_year: equipment.operation_duration_per_year
    };
  }

  public coerceEquipmentAPIToEquipment(equipmentAPI: EquipmentAPI): Equipment {
   /* Equipment fields not available in EquipmentAPI
          parent_equipment?: Equipment[];
          zone?: Maybe<Zone>;
          files?: VirtualFile[];
          primary_device?: Device;
          favourites?: Favourite[];
          metadata_type?: MetadataType
          collector?: Collector
    */
    return {
      equipment_id: equipmentAPI.equipment_id,
      canonical_favourite_ids: equipmentAPI.canonical_favourite_ids,
      capacity: equipmentAPI.capacity,
      capacity_unit: equipmentAPI.capacity_unit,
      collector_id: equipmentAPI.collector_id,
      comment: equipmentAPI.comment,
      energyconsumption: equipmentAPI.energyconsumption,
      energyconsumption_unit: equipmentAPI.energyconsumption_unit,
      favourite_ids: equipmentAPI.favourite_ids,
      is_active: equipmentAPI.is_active,
      metadata_type_id: equipmentAPI.metadata_type_id,
      model: equipmentAPI.model,
      name: equipmentAPI.name,
      related_equipment: equipmentAPI.related_equipment,
      role: equipmentAPI.role,
      shared: equipmentAPI.shared,
      vendor: equipmentAPI.vendor,
      virtual: equipmentAPI.virtual,
      primary_device_id: equipmentAPI.primary_device_id,
      file_ids: equipmentAPI.files,
      zone_id: equipmentAPI.zone_id,
      target_running_load: equipmentAPI.target_running_load,
      parent_equipment_ids: equipmentAPI.parent_equipment_ids,
      maintainer_name: equipmentAPI.maintainer_name,
      maintainer_reference: equipmentAPI.maintainer_reference,
      operation_duration_per_year: equipmentAPI.operation_duration_per_year
    };
  }

  public buildCompleteEquipmentObject(equipment: Equipment,
                                      metadataTypesById: Map<number, MetadataType>,
                                      devicesById: Map<number, Device>,
                                      filesById: Map<number, VirtualFile>,
                                      zonesById: Map<number, Zone>,
                                      virtual: boolean): Equipment {
    return {
      equipment_id: equipment.equipment_id,
      favourite_ids: equipment.favourite_ids ?? [],
      canonical_favourite_ids: equipment.canonical_favourite_ids ?? [],
      collector_id: equipment.collector_id,
      is_active: true,
      virtual: virtual,
      role: equipment.role ?? null,
      energyconsumption: equipment.energyconsumption ?? null,
      energyconsumption_unit: equipment.energyconsumption_unit ?? null,
      vendor: equipment.vendor ?? null,
      target_running_load: equipment.target_running_load ?? null,
      maintainer_name: equipment.maintainer_name ?? null,
      maintainer_reference: equipment.maintainer_reference ?? null,
      operation_duration_per_year: equipment.operation_duration_per_year ?? null,
      capacity: equipment.capacity ?? null,
      capacity_unit: equipment.capacity_unit ?? null,
      name: equipment.name,
      comment: equipment.comment ?? null,
      model: equipment.model,
      shared: equipment.shared,
      parent_equipment_ids: equipment.parent_equipment_ids ?? [],
      related_equipment: equipment.related_equipment ?? [],
      zone_id: equipment.zone_id ?? null,
      zone: equipment.zone_id ? zonesById?.get(equipment.zone_id) : null,
      metadata_type_id: equipment.metadata_type_id,
      metadata_type: equipment.metadata_type_id ? metadataTypesById?.get(equipment.metadata_type_id) : null,
      ...virtual && {file_ids: equipment.file_ids ?? null},
      ...(virtual && 'file_ids' in equipment) && {files: equipment.file_ids?.map((fileId: number) => filesById?.get(fileId)) ?? null},
      ...!virtual && {primary_device_id: equipment.primary_device_id ?? null},
      ...(!virtual && 'primary_device_id' in equipment) && {primary_device: devicesById?.get(equipment.primary_device_id) ?? null}
    };
  }

  public sortByEquipmentName = (a: Equipment, b: Equipment) => (a?.name ?? '').localeCompare(b?.name ?? '');

  public getEquipmentAggregation(searchTerm: EquipmentAggregationSearchTerm): Observable<EquipmentAggregation[]> {
    const options = {headers: this.getHeaders(), params: objectToHttpParams(searchTerm)};
    return this.http.get<APIResponse<EquipmentAggregationResponse>>(this.getEquipmentEndpoint() + '/aggregation', options)
      .pipe(map(response => response[this.DATA_PREFIX]?.equipment_aggregation));
  }

  private getEquipmentEndpoint(): string {
    return this.getGateway() + '/equipment';
  }

  private getEquipmentItemEndpoint(equipmentId: number): string {
    return this.getEquipmentEndpoint() + '/' + equipmentId;
  }

  private getEquipmentPropertiesEndpoint(equipmentId: EquipmentId): string {
    return this.getEquipmentItemEndpoint(equipmentId) + '/properties';
  }
}
